import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { listHrContracts, listHrPeople, listPayrollMovements, peopleCostSynthesis } from '../../api/hr';
import { formatNumber, formatMoney } from '../../utils/format';
import { ALL_M, parseLocalDate } from './peopleMonthly';
import {
  buildBaseCells, applyPersonMovements, buildSyntheticRow, pickReferenceContract,
} from './planningEngine';
import PageShell from '../../components/PageShell';
import PeopleImportModal, { Ico } from './PeopleImportModal';
import CardShell from '../../components/CardShell';
import SegmentedToggle from '../../components/SegmentedToggle';
import './HrPage.css';
import './HeadcountSummary.css';

/* Synthèse des effectifs — où j'en suis, où je vais, ce qui bouge.

   Trois lectures d'une seule question, l'effectif en EPT :
     · les tuiles disent l'EXERCICE (départ au 01.01, aujourd'hui, moyenne) ;
     · la frise mensuelle le déroule, réel puis prévisionnel.

   Le DÉTAIL des mouvements n'est pas ici (retiré le 2026-08-29) : la liste
   allongeait l'écran d'autant de lignes qu'il y a de contrats datés, quand la
   synthèse doit tenir d'un regard. Ce qui bouge se lit dans l'écart au 01.01,
   et se détaille dans l'onglet Mouvements.

   La ligne traitillée reste au niveau du 01.01 de l'exercice quelle que soit
   la fenêtre choisie : c'est le point de départ, pas le premier mois affiché.
   Sur N-1 elle traverse donc aussi le passé, et l'on voit d'où l'on vient.

   La projection passe par `planningEngine` — le même code que la section
   Prévisionnel. Deux écrans qui projettent le même effectif ne peuvent pas
   répondre deux chiffres différents. */

const AXIS = [17, 16, 15, 14];   // repères d'axe recalculés à l'échelle réelle

export default function HeadcountSummaryPage({ activeTenant, year, unit = 'chf', embedded = false, addToast = null,
                                              onAddPerson = null, onAddMove = null, onGoContracts = null }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const MONTHS = useMemo(() => Array.from({ length: 12 }, (_, i) => t(`common:months.${i + 1}`)), [t]);

  const [contracts, setContracts] = useState([]);
  const [people, setPeople] = useState([]);
  const [movements, setMovements] = useState([]);
  /* Masse salariale chargée par année (rémunération + charges sociales
     employeur), lue à la SOURCE de la section Charges : une seule définition
     de la masse dans l'application. Elle suit les CONTRATS — l'endpoint ne
     connaît pas les mouvements simulés, ce que les tuiles disent. */
  const [mass, setMass] = useState({});
  const [loading, setLoading] = useState(false);
  /* Fenêtre de lecture : l'exercice seul, ou deux ans en arrière comme en
     avant. C'est une VUE, pas un filtre : elle change ce que la frise dessine,
     elle ne retranche aucune ligne. Sa place est donc l'entête (2026-08-29) —
     la carte n'a d'ailleurs aucun filtre, sa toolbar disparaît. */
  const [win, setWin] = usePersistentValue(`hc_summary_win_${tenantId}`, 'n');
  const [showData, setShowData] = useState(false);
  /* Vue : ce que disent les CONTRATS signés, ou ce qu'y ajoute la SIMULATION.
     Par défaut la simulation — c'est l'écran d'un dirigeant qui prépare, pas
     d'un comptable qui constate ; les contrats restent à un clic. */
  const [mode, setMode] = usePersistentValue(`hc_summary_mode_${tenantId}`, 'sim');

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    Promise.all([listHrContracts(tenantId), listHrPeople(tenantId), listPayrollMovements(tenantId)])
      .then(([c, pe, mv]) => {
        if (!c.error) setContracts(c.data || []);
        if (!pe.error) setPeople(pe.data || []);
        if (!mv.error) setMovements(mv.data || []);
      })
      .finally(() => setLoading(false));
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    let alive = true;
    const yrs = [year - 1, year, year + 1];
    Promise.all(yrs.map(y => peopleCostSynthesis(tenantId, y))).then((res) => {
      if (!alive) return;
      const out = {};
      res.forEach((r, i) => {
        if (r.error) return;
        const rows = (r.data?.rows || r.rows || []);
        out[yrs[i]] = rows.reduce((a, row) => a + Number(row.loaded_payroll || 0), 0);
      });
      setMass(out);
    });
    return () => { alive = false; };
  }, [tenantId, year]);

  const closedThrough = activeTenant?.last_closed_period
    ? parseLocalDate(activeTenant.last_closed_period) : null;
  const firstOpen = closedThrough
    ? new Date(closedThrough.getFullYear(), closedThrough.getMonth() + 1, 1) : null;
  const isOpenAt = (y, m) => !firstOpen || new Date(y, m, 1) >= firstOpen;

  const years = useMemo(() => (
    win === 'prev' ? [year - 1, year] : win === 'next' ? [year, year + 1] : [year]
  ), [win, year]);

  /* Série mensuelle d'EPT et de têtes, DEUX FOIS : ce que disent les contrats
     signés, et ce qu'y ajoutent les mouvements simulés (2026-08-29). Les deux
     coïncident sur les mois clos — un mouvement ne réécrit jamais le passé —
     et divergent sur l'avenir : c'est là qu'on lit ce que la simulation
     change. Trois années sont toujours calculées : les tuiles parlent de
     l'exercice et de N-1, quelle que soit la fenêtre affichée. */
  const series = useMemo(() => {
    const byPerson = new Map();
    for (const c of contracts) {
      if (!c.person_id) continue;
      if (!byPerson.has(c.person_id)) byPerson.set(c.person_id, []);
      byPerson.get(c.person_id).push(c);
    }
    const mvByPerson = new Map();
    for (const mv of movements) {
      if (!mv.person_id) continue;
      if (!mvByPerson.has(mv.person_id)) mvByPerson.set(mv.person_id, []);
      mvByPerson.get(mv.person_id).push(mv);
    }
    const out = {};
    for (const y of [year - 1, year, year + 1]) {
      const acc = { contract: { fte: ALL_M.map(() => 0), heads: ALL_M.map(() => 0) },
                    sim:      { fte: ALL_M.map(() => 0), heads: ALL_M.map(() => 0) } };
      for (const person of people) {
        const pc = byPerson.get(person.id) || [];
        const ref = pickReferenceContract(pc);
        const ar = ref ? Number(ref.activity_rate || 0) : 0;
        const salary100 = (ref && ar > 0) ? Number(ref.monthly_salary || 0) / ar : 0;
        const base = buildBaseCells(person, pc, y);
        const mvs = mvByPerson.get(person.id) || [];
        const open = (m) => isOpenAt(y, m);
        base.forEach((c, m) => {
          if (c.pct > 0) { acc.contract.fte[m] += c.pct / 100; acc.contract.heads[m] += 1; }
        });
        applyPersonMovements(base, salary100, mvs, y, open).forEach((c, m) => {
          if (c.pct > 0) { acc.sim.fte[m] += c.pct / 100; acc.sim.heads[m] += 1; }
        });
      }
      /* Positions ouvertes et réductions non nommées : elles n'ont pas de
         personne, donc pas de contrat — elles n'existent que dans la
         simulation, et s'y ajoutent en propre. */
      for (const mv of movements) {
        if (mv.person_id) continue;
        buildSyntheticRow(mv, y, (m) => isOpenAt(y, m)).cells.forEach((c, m) => {
          if (c.pct === 0) return;
          acc.sim.fte[m] += c.pct / 100;
          acc.sim.heads[m] += c.pct > 0 ? 1 : -1;
        });
      }
      out[y] = acc;
    }
    return out;
  }, [people, contracts, movements, year, activeTenant?.last_closed_period]); // eslint-disable-line

  const read = (y) => series[y][mode];

  const view = useMemo(() => {
    const pts = [];
    years.forEach((y) => series[y][mode].fte.forEach((v, m) => pts.push({ y, m, fte: v, open: !isOpenAt(y, m) })));
    return pts;
  }, [years, series, mode]); // eslint-disable-line

  const anchor = read(year).fte[0];                   // EPT au 01.01 de l'exercice
  const avg = (a) => a.reduce((s, v) => s + v, 0) / a.length;
  const avgN = avg(read(year).fte);
  const avgPrev = avg(read(year - 1).fte);
  const lastYear = years[years.length - 1];
  const last = read(lastYear).fte[11];

  /* Trois segments d'axe, pas deux (2026-08-29) : l'année ANTÉRIEURE n'est pas
     « du réalisé » de l'exercice qu'on lit, c'est le passé auquel on se
     compare. Chaque segment a son bandeau et sa couleur de colonne. */
  const segOf = (pt) => (pt.y < year ? 'past' : (pt.open ? 'real' : 'fore'));
  const bands = useMemo(() => {
    const out = [];
    view.forEach((pt) => {
      const k = segOf(pt);
      const lastBand = out[out.length - 1];
      if (lastBand && lastBand.kind === k) lastBand.n += 1;
      else out.push({ kind: k, n: 1 });
    });
    return out;
  }, [view, year]); // eslint-disable-line
  const BAND_LBL = {
    past: p('Année antérieure'),
    real: `${p('Réalisé')} ${year}`,
    fore: p('Prévisionnel'),
  };
  /* L'axe PART DE ZÉRO (2026-08-29) : sur un effectif, une base tronquée
     transforme une variation d'un demi-poste en falaise. Le haut garde une
     marge pour le chiffre posé sur la colonne. */
  const lo = 0;
  const peak = Math.max(...view.map(pt => pt.fte), anchor, 1);
  const step = peak <= 6 ? 1 : peak <= 15 ? 2 : peak <= 35 ? 5 : peak <= 70 ? 10 : 20;
  const hi = Math.ceil((peak * 1.12) / step) * step;
  const at = (v) => (1 - (v - lo) / (hi - lo)) * 100;
  const ticks = useMemo(() => {
    const out = [];
    for (let v = 0; v <= hi; v += step) out.push(v);
    return out;
  }, [hi, step]);

  const money = (v) => formatMoney(v, unit, 0);
  const chfLbl = unit === 'kchf' ? 'kCHF' : 'CHF';
  /* Variation en % : sans année de référence chargée, pas de pourcentage
     inventé — la tuile reste muette. */
  const varPct = (a, b) => (a != null && b) ? ((a - b) / b) * 100 : null;
  const massN = mass[year];
  const massPrev = mass[year - 1];
  const massNext = mass[year + 1];
  const pctN = varPct(massN, massPrev);
  const pctNext = varPct(massNext, massN);

  const winLabel = years.length > 1 ? `${years[0]} – ${years[1]}` : `${year}`;
  const fmt1 = (v) => formatNumber(v, 1);
  const signed = (v) => `${v >= 0 ? '+' : '−'}${fmt1(Math.abs(v))}`;

  if (!tenantId) {
    return (
      <PageShell embedded={embedded} framed={!embedded} title={p('Synthèse des effectifs')}>
        <div className="hr-page__empty">{p('Sélectionner une entreprise.')}</div>
      </PageShell>
    );
  }

  return (
    <PageShell embedded={embedded} framed={!embedded} title={p('Synthèse des effectifs')}>
      <main className="hr-page is-embedded">
        {loading && <div className="hr-page__loading">{t('common:status.loading')}</div>}
        {/* ENTRÉES de la section : elles MÈNENT là où la saisie vit déjà —
            rejouer les formulaires ici ferait un second endroit pour écrire la
            même chose. Deux natures, jamais mélangées : le contrat signé et le
            mouvement simulé. */}
        {!loading && (onAddPerson || onAddMove || onGoContracts) && (
          <div className="hcs-doors">
            {/* Le panneau « Données People » EXISTE (PeopleImportModal) et
                range déjà la saisie par nature : ajouter une personne, en
                importer plusieurs, charger extras, frais, indemnités. On
                l'ouvre plutôt que d'aligner une tuile par porte. */}
            {onAddPerson && (
              <button type="button" className="hcs-door" data-tour="headcount-door-data" onClick={() => setShowData(true)}>
                <Ico k="users" />
                <span><b>{p('Mettre à jour les données')}</b>
                <small>{p('collaborateurs, contrats, extras, frais')}</small></span>
              </button>
            )}
            {onAddMove && (
              <button type="button" className="hcs-door" data-tour="headcount-door-move" onClick={onAddMove}>
                <Ico k="move" />
                <span><b>{p('Poser un mouvement')}</b>
                <small>{p('engagement, départ, changement de taux')}</small></span>
              </button>
            )}
            {onGoContracts && (
              <button type="button" className="hcs-door" onClick={onGoContracts}>
                <Ico k="contract" />
                <span><b>{p('Gérer les contrats')} ›</b>
                <small>{p('modifier un contrat existant')}</small></span>
              </button>
            )}
          </div>
        )}
        {!loading && (
          <CardShell
            title={p('Synthèse des effectifs')}
            subtitle={winLabel}
            viewControls={(
              <div className="hcs__views" data-tour="headcount-views">
              <SegmentedToggle
                compact
                label={p('Vue')} value={mode} onChange={setMode}
                options={[
                  { value: 'contract', label: p('Contrats') },
                  { value: 'sim',      label: p('Simulation') },
                ]}
              />
              <SegmentedToggle
                compact
                label={p('Période')} value={win} onChange={setWin}
                options={[
                  { value: 'prev', label: `${year - 1} · ${year}` },
                  { value: 'n',    label: `${year}` },
                  { value: 'next', label: `${year} · ${year + 1}` },
                ]}
              />
              </div>
            )}
            summary={(
              <div className="hcs__kpis" data-tour="headcount-kpis">
                {/* La tuile « Aujourd'hui » est retirée (2026-08-29) : sur un
                    exercice clos elle affichait décembre, et sur l'exercice en
                    cours elle répétait ce que la frise montre déjà. */}
                {/* Le départ et l'arrivée dans UNE tuile (2026-08-29) : deux
                    tuiles pour les deux bouts d'un même trajet obligeaient à
                    faire la soustraction de tête. */}
                <div className="hcs__kpi hcs__kpi--focus">
                  <div className="hcs__kpi-lbl">
                    {p('Au 01.01')} {year} → {MONTHS[11]} {lastYear}
                  </div>
                  <div className="hcs__kpi-val">
                    {fmt1(anchor)} <span className="hcs__arrow" aria-hidden>→</span> {fmt1(last)} <span>EPT</span>
                  </div>
                  <div className="hcs__kpi-hint">
                    {signed(last - anchor)} EPT · {read(year).heads[0]} → {read(lastYear).heads[11]} {p('collaborateurs')}
                  </div>
                </div>
                <div className="hcs__kpi">
                  <div className="hcs__kpi-lbl">{p('Moyenne')} {year}</div>
                  <div className="hcs__kpi-val">{fmt1(avgN)} <span>EPT</span></div>
                  <div className="hcs__kpi-hint">{signed(avgN - avgPrev)} {p('vs')} {year - 1}</div>
                </div>
                {/* La masse chargée referme la lecture : l'effectif est un
                    nombre de personnes, la masse est ce qu'il coûte. */}
                <div className="hcs__kpi">
                  <div className="hcs__kpi-lbl">{p('Masse chargée')} {year}</div>
                  <div className="hcs__kpi-val">
                    {massN == null ? '—' : money(massN)} <span>{chfLbl}</span>
                  </div>
                  <div className="hcs__kpi-hint">
                    {pctN == null ? p('contrats') : `${signed(pctN)}% ${p('vs')} ${year - 1} · ${p('contrats')}`}
                  </div>
                </div>
                <div className="hcs__kpi">
                  <div className="hcs__kpi-lbl">{p('Masse chargée')} {year + 1}</div>
                  <div className="hcs__kpi-val">
                    {massNext == null ? '—' : money(massNext)} <span>{chfLbl}</span>
                  </div>
                  <div className="hcs__kpi-hint">
                    {pctNext == null ? p('contrats') : `${signed(pctNext)}% ${p('vs')} ${year} · ${p('contrats')}`}
                  </div>
                </div>
              </div>
            )}
          >
            <div className="hcs__chart" data-tour="headcount-chart">
              <div className="hcs__bands">
                <div className="hcs__gutter" />
                {bands.map((b, i) => (
                  <div key={i} className={`hcs__band hcs__band--${b.kind}`} style={{ flex: b.n }}>
                    {BAND_LBL[b.kind]}
                  </div>
                ))}
              </div>
              <div className="hcs__plot">
                <div className="hcs__bars">
                  <div className="hcs__gutter hcs__axis">
                    {/* L'unité se dit une fois, en tête d'axe : sans elle,
                        une échelle de nombres nus ne se lit pas. */}
                    <span className="hcs__axis-unit">EPT</span>
                    {ticks.map(v => (
                      <span key={v} style={{ top: `calc(${at(v)}% - 6px)` }}>{v}</span>
                    ))}
                  </div>
                  {view.map((pt, i) => {
                    const h = Math.max(2, (pt.fte - lo) / (hi - lo) * 100);
                    /* La valeur se pose DANS la colonne (2026-08-30). Sous 8 %
                       de hauteur elle n'y tiendrait pas : elle repasse
                       au-dessus plutôt que d'être rognée. */
                    const inside = h >= 8;
                    const val = <div className={`hcs__val${inside ? ' hcs__val--in' : ''}`}>{fmt1(pt.fte)}</div>;
                    return (
                      <div key={i} className={`hcs__slot${(i % 12 === 11 && i < view.length - 1) ? ' hcs__slot--edge' : ''}`}>
                        {!inside && val}
                        <div
                          className={`hcs__bar hcs__bar--${segOf(pt)}`}
                          style={{ height: `${h}%` }}
                          title={`${fmt1(pt.fte)} EPT — ${MONTHS[pt.m]} ${pt.y}`}
                        >
                          {inside && val}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="hcs__anchor" style={{ top: `${at(anchor)}%` }} />
                <div className="hcs__anchor-lbl" style={{ top: `calc(${at(anchor)}% - 15px)` }}>
                  01.01.{year} · {fmt1(anchor)}
                </div>
              </div>
              <div className="hcs__axis-x">
                <div className="hcs__gutter" />
                {view.map((pt, i) => (
                  <div key={i} className={`hcs__tick${pt.m === 0 ? ' hcs__tick--year' : ''}`}>
                    {pt.m === 0 ? pt.y : MONTHS[pt.m]}
                  </div>
                ))}
              </div>
            </div>

          </CardShell>
        )}
        {showData && activeTenant && (
          <PeopleImportModal
            tenantId={tenantId}
            year={year}
            addToast={addToast}
            onAddPerson={() => { setShowData(false); if (onAddPerson) onAddPerson(); }}
            onClose={() => setShowData(false)}
          />
        )}
      </main>
    </PageShell>
  );
}
